import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import moment, {Moment} from 'moment';
import {ComboBox, Detail, Modal, Text, TextField} from '@forecast-it/design-system';
import InlineLoader from '../../../forecast-app/shared/components/inline-loader/inline_loader';
import {MODAL_TYPE, showModal} from '../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {hasPermission} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {
	fetchUnlinkedXeroInvoices,
	fetchXeroOrganisations,
	XeroOrganisation,
} from '../../../components/insights/invoicing/importFromXero';
import {
	DateInput,
	FilterField,
	FilterRow,
	ListArea,
	LoadingArea,
	OrganisationInput,
	SelectionNote,
	VisuallyHidden,
} from './XeroInvoiceImportModal.styled';
import {ImportDateRange, InvoiceDateRange} from './ImportDateRange';
import {organisationMessage, UnlinkedInvoiceList, UnlinkedListState} from './UnlinkedInvoiceList';
import {
	headerState,
	refreshSelection,
	SelectableInvoice,
	SelectedInvoices,
	selectionCounts,
	toggleInvoice,
	toggleVisible,
} from './invoiceSelection';
import {
	canSelectFor,
	ContactPair,
	contactKey,
	contactsToAsk,
	decorate,
	effectiveMatches,
	fetchInvoiceClientMatches,
	isKept,
	KnownMatches,
	MAX_CONTACTS_PER_REQUEST,
	matchDisplay,
} from './clientMatch';

/*
 * The Import Invoices and Payments from Xero modal, opened from All invoices. It lists the Xero sales
 * invoices dated in the chosen range that no Forecast invoice links to, to tick for import. Ticks are
 * kept across range changes; Create counts them, and what it does comes later. Choosing an
 * organisation comes later too. Each row's Client match is asked after its list loads, per Xero
 * contact. Everything it holds lives here, so each open starts fresh with nothing ticked or matched.
 */

const ALL_ORGANISATIONS = 'all';
const XERO_INTEGRATION_PAGE = '/admin/integrations/xero';

/** The default Invoice date range: the month ending today. ISO dates. */
export const defaultInvoiceDateRange = (today: Moment): {startDate: string; endDate: string} => ({
	startDate: today.clone().subtract(1, 'month').format('YYYY-MM-DD'),
	endDate: today.clone().format('YYYY-MM-DD'),
});

/** What the modal is opened with. Nothing yet. */
export type XeroInvoiceImportModalPayload = Record<string, never>;

/** Opens the modal through the generic modal conductor. */
export const showXeroInvoiceImportModal = (props: XeroInvoiceImportModalPayload = {}): void => {
	showModal({type: MODAL_TYPE.XERO_INVOICE_IMPORT, ...props});
};

type Props = {
	closeModal: () => void;
};

const XeroInvoiceImportModal = ({closeModal}: Props): JSX.Element | null => {
	const {formatMessage} = useIntl();
	const history = useHistory();
	// Organisations Forecast can reach; null while they load.
	const [organisations, setOrganisations] = useState<XeroOrganisation[] | null>(null);
	// Every connected organisation's name, reachable or not, for the lines about ones not listed.
	const [organisationNames, setOrganisationNames] = useState<Map<string, string>>(() => new Map());
	const [dateRange, setDateRange] = useState<InvoiceDateRange>(() => defaultInvoiceDateRange(moment()));
	const [list, setList] = useState<UnlinkedListState>({state: 'loading'});
	// The ticked invoices, each with a copy of its row, from any range. What Create hands on later.
	const [selected, setSelected] = useState<SelectedInvoices>(() => new Map());
	const open = useRef(true);
	// Only the newest request's answer is used: a slow answer for an older range must not replace it.
	const latestRequest = useRef(0);
	const isAdminUser = useMemo(() => hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS), []);
	const hasClientCreate = useMemo(() => hasPermission(PERMISSION_TYPE.CLIENT_CREATE), []);
	// The Client match answers, per contact, for this open. The ref is what the asking loop reads.
	const [known, setKnownState] = useState<KnownMatches>(() => new Map());
	const knownRef = useRef<KnownMatches>(known);
	const setKnown = useCallback((update: (current: KnownMatches) => KnownMatches) => {
		knownRef.current = update(knownRef.current);
		setKnownState(knownRef.current);
	}, []);
	// Contacts waiting to be asked, newest list first, and whether a request is out. One at a time.
	const toAsk = useRef<ContactPair[]>([]);
	const asking = useRef(false);
	// The contacts of the request that is out: a list loading meanwhile must not queue them again.
	const inFlight = useRef<ReadonlySet<string>>(new Set());

	const askNextBatch = useCallback(async () => {
		asking.current = true;
		while (open.current && toAsk.current.length > 0) {
			const batch = toAsk.current.slice(0, MAX_CONTACTS_PER_REQUEST);
			toAsk.current = toAsk.current.slice(MAX_CONTACTS_PER_REQUEST);
			inFlight.current = new Set(batch.map(contactKey));
			const answers = await fetchInvoiceClientMatches(batch);
			inFlight.current = new Set();
			if (!open.current) {
				break;
			}
			const byKey = new Map((answers || []).map(answer => [contactKey(answer), answer]));
			setKnown(current => {
				const next = new Map(current);
				batch.forEach(contact => {
					const key = contactKey(contact);
					const answer = byKey.get(key);
					if (answer) {
						next.set(key, answer);
					} else if (!isKept(current.get(key))) {
						// A failed request, or a contact the answer leaves out, is unknown: asked again on the
						// next load. It never replaces a kept answer, whose row may be ticked already.
						next.set(key, {...contact, match: 'unknown'});
					}
				});
				return next;
			});
		}
		asking.current = false;
	}, [setKnown]);

	/** Queues this list's contacts with no kept answer, ahead of older lists, and starts asking. */
	const askMatches = useCallback(
		(rows: SelectableInvoice[]) => {
			const fresh = contactsToAsk(rows, knownRef.current).filter(contact => !inFlight.current.has(contactKey(contact)));
			const freshKeys = new Set(fresh.map(contactKey));
			toAsk.current = [...fresh, ...toAsk.current.filter(contact => !freshKeys.has(contactKey(contact)))];
			// An unknown asked again shows the skeleton until its new answer.
			if (fresh.some(contact => knownRef.current.has(contactKey(contact)))) {
				setKnown(current => {
					const next = new Map(current);
					freshKeys.forEach(key => next.delete(key));
					return next;
				});
			}
			if (!asking.current) {
				askNextBatch();
			}
		},
		[askNextBatch, setKnown]
	);

	const showReconnectWindow = useCallback(() => {
		closeModal();
		showModal({type: MODAL_TYPE.XERO_INACCESSIBLE, isAdminUser});
	}, [closeModal, isAdminUser]);

	const loadInvoices = useCallback(
		(range: InvoiceDateRange) => {
			const request = ++latestRequest.current;
			setList({state: 'loading'});
			fetchUnlinkedXeroInvoices(range).then(data => {
				if (!open.current || request !== latestRequest.current) {
					return;
				}
				if (data === null) {
					setList({state: 'error'});
					return;
				}
				if (data.organisations.every(org => org.status === 'inaccessible')) {
					// Every connection expired or went since the modal opened (none left counts too): as on
					// open, the reconnect window takes over, never "No new invoices".
					showReconnectWindow();
					return;
				}
				// The range this answer is for: the modal's dateRange may already hold a newer one.
				setSelected(current => refreshSelection(current, data, range));
				setList({state: 'ready', data});
				askMatches(data.invoices);
			});
		},
		[showReconnectWindow, askMatches]
	);

	useEffect(() => {
		open.current = true;
		// Read again on every open: the token can expire after the page loaded.
		fetchXeroOrganisations().then(orgs => {
			if (!open.current) {
				return;
			}
			const reachable = (orgs || []).filter(org => !org.inaccessible);
			if (reachable.length === 0) {
				// Expired, revoked, or Xero could not be read: the existing reconnect window says
				// what to do, and nothing can be created from here.
				showReconnectWindow();
				return;
			}
			setOrganisationNames(new Map((orgs || []).map(org => [org.tenantId, org.name])));
			setOrganisations(reachable);
			loadInvoices(dateRange);
		});
		return () => {
			open.current = false;
		};
		// Once per open.
	}, []);

	const acceptDateRange = (range: InvoiceDateRange): void => {
		setDateRange(range);
		loadInvoices(range);
	};

	// The rows the list draws: what the header checkbox and the note act on.
	const visible: SelectableInvoice[] = useMemo(() => (list.state === 'ready' ? list.data.invoices : []), [list]);
	// Each contact's match with the selection applied: two ticked contacts can turn each other into review.
	const effective = useMemo(() => effectiveMatches(known, selected), [known, selected]);
	// The ticks as Create and the counts read them: each carrying its current match.
	const matchedSelection = useMemo(
		() => new Map(Array.from(selected, ([key, row]) => [key, decorate(row, effective)])),
		[selected, effective]
	);
	const matchOf = useCallback((row: SelectableInvoice) => matchDisplay(row, known, effective), [known, effective]);
	const canSelect = useMemo(() => canSelectFor(hasClientCreate, known), [hasClientCreate, known]);
	const counts = selectionCounts(matchedSelection, visible);
	// Not while the list loads: it would flash on every reload.
	const selectionNote =
		list.state !== 'loading' && counts.outsideView > 0
			? formatMessage({id: 'xero_invoice_import.selected_outside_view'}, {count: counts.outsideView})
			: '';
	// Stable, so the memoised rows re-draw only when their own tick changes.
	const toggleRow = useCallback((row: SelectableInvoice) => setSelected(current => toggleInvoice(current, row)), []);
	const toggleShown = (): void => setSelected(current => toggleVisible(current, visible, canSelect));
	const createLabel =
		counts.total === 0
			? formatMessage({id: 'xero_invoice_import.create_invoices'})
			: counts.newClients === 0
			? formatMessage({id: 'xero_invoice_import.create_invoice_count'}, {invoices: counts.total})
			: formatMessage(
					{id: 'xero_invoice_import.create_invoices_and_clients'},
					{invoices: counts.total, clients: counts.newClients}
			  );

	// Said by one region that is always there: a region that appears already holding its text is not
	// announced by many screen readers.
	const announcement =
		list.state === 'loading'
			? formatMessage({id: 'xero_invoice_import.loading_invoices'})
			: list.state === 'error'
			? [formatMessage({id: 'xero_invoice_import.load_failed'}), selectionNote].filter(Boolean).join(' ')
			: [
					...list.data.organisations
						.filter(org => org.status !== 'ok')
						.map(org => organisationMessage(org, organisationNames, isAdminUser, formatMessage)),
					selectionNote,
			  ]
					.filter(Boolean)
					.join(' ');

	const reconnect = (): void => {
		closeModal();
		history.push(XERO_INTEGRATION_PAGE);
	};

	const organisationLabel = formatMessage({id: 'xero_invoice_import.organisation'});
	const allOrganisations = formatMessage({id: 'xero_invoice_import.all_organisations'});
	const invoiceDateLabel = formatMessage({id: 'xero_invoice_import.invoice_date'});

	return (
		<Modal size={Modal.SIZE.EXTRA_LARGE} onCloseModal={closeModal} setHeight>
			<Modal.Title>{formatMessage({id: 'xero_invoice_import.title'})}</Modal.Title>
			<Modal.Content>
				<Text>{formatMessage({id: 'xero_invoice_import.description'})}</Text>
				{organisations === null ? (
					<LoadingArea data-cy="xero-import-loading">
						<InlineLoader />
					</LoadingArea>
				) : (
					<FilterRow data-cy="xero-import-filters">
						<FilterField data-cy="xero-import-organisation-field">
							<Text>{organisationLabel}</Text>
							<OrganisationInput>
								{organisations.length === 1 ? (
									// One organisation: its name, read only, no dropdown.
									<TextField
										aria-label={organisationLabel}
										value={organisations[0].name}
										disabled
										width={TextField.WIDTH.FLEX}
										data-cy="xero-import-organisation"
									/>
								) : (
									// Several: All organisations. Choosing one comes later.
									<ComboBox
										aria-label={organisationLabel}
										defaultItems={[{id: ALL_ORGANISATIONS, name: allOrganisations}]}
										value={ALL_ORGANISATIONS}
										disabled
										isDisabled
										width={ComboBox.WIDTH.FLEX}
										data-cy="xero-import-organisation"
									>
										{(item: {id: string; name: string}) => (
											<ComboBox.Item key={item.id} textValue={item.name}>
												<Text>{item.name}</Text>
											</ComboBox.Item>
										)}
									</ComboBox>
								)}
							</OrganisationInput>
						</FilterField>
						<FilterField>
							<Text>{invoiceDateLabel}</Text>
							<DateInput>
								<ImportDateRange range={dateRange} onAccept={acceptDateRange} />
							</DateInput>
						</FilterField>
					</FilterRow>
				)}
				<VisuallyHidden role="status" data-cy="xero-import-list-status">
					{organisations === null ? '' : announcement}
				</VisuallyHidden>
				<ListArea data-cy="xero-import-list" aria-busy={organisations !== null && list.state === 'loading'}>
					{/* Plain text: the status region above says it to screen readers. */}
					<SelectionNote data-cy="xero-import-selection-note">
						{selectionNote ? <Detail color="medium">{selectionNote}</Detail> : null}
					</SelectionNote>
					{organisations === null ? null : (
						<UnlinkedInvoiceList
							list={list}
							organisationNames={organisationNames}
							isAdmin={isAdminUser}
							onRetry={() => loadInvoices(dateRange)}
							onReconnect={reconnect}
							selected={selected}
							headerState={headerState(selected, visible, canSelect)}
							hasSelection={counts.total > 0}
							canSelect={canSelect}
							onToggle={toggleRow}
							onToggleVisible={toggleShown}
							matchOf={matchOf}
							canCreateClients={hasClientCreate}
						/>
					)}
				</ListArea>
			</Modal.Content>
			<Modal.Footer>
				<Modal.SecondaryFooterButton onPress={closeModal} dataCy="xero-import-cancel">
					{formatMessage({id: 'common.cancel'})}
				</Modal.SecondaryFooterButton>
				{/* Enabled with something ticked; what Create does with the ticked rows comes later. */}
				<Modal.PrimaryFooterButton disabled={counts.total === 0} dataCy="xero-import-create">
					{createLabel}
				</Modal.PrimaryFooterButton>
			</Modal.Footer>
		</Modal>
	);
};

export default XeroInvoiceImportModal;
